import {
  Box,
  Button,
  Center,
  Flex,
  HStack,
  ListItem,
  Spinner,
  Text,
  UnorderedList,
  VStack,
} from '@chakra-ui/react';
import { useMemo } from 'react';
import { Trans } from '@lingui/react/macro';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { CourseKindSchema } from '@duke-hq/svc-api-db/schemas/enums/CourseKind.schema';
import {
  FormContentContainer,
  FormFooterContainer,
  FormMainContainer,
} from '@duke-hq/ui/components/Modals/FormModalLayouts';
import { StudentScheduleItem } from '@duke-hq/svc-api/student/services/schedulerService';
import { trpcTenant } from '@duke-hq/app-provider-web/tenant/trpcTenant';
import { useBulkInternalEnrolments } from '@duke-hq/app-provider-web/tenant/components/BulkEnrolments/useBulkInternalEnrolments';
import type { RouterOutputs } from '@duke-hq/svc-api/provider';

type ReviewCourseProps = {
  goToNextStep: () => void;
  goToPrevStep: () => void;
};

interface CourseReviewProps {
  data: RouterOutputs['courses']['byId'] | undefined;
  title: string;
  identifier: string;
}

function CourseUnitOfCompetency({ unit }: { unit: StudentScheduleItem }) {
  return (
    <Flex
      align="start"
      justify="center"
      direction="column"
      border="1px solid"
      borderColor="border.default"
      borderRadius="8px"
      padding="16px"
      bg="bg.subtle"
      w="full"
    >
      {unit?.['Unit Code'] ? (
        <Text color="fg.muted" fontSize="sm">
          {unit['Unit Code']}
        </Text>
      ) : null}
      <VStack align="start" justify="start" spacing="4px">
        <Text color="fg.emphasis" textStyle="md" fontWeight="semibold">
          {unit.Title}
        </Text>
      </VStack>
    </Flex>
  );
}

function CourseReview({ data, title, identifier }: CourseReviewProps) {
  const { selectedCourseAttributes } = useBulkInternalEnrolments();
  const { hasWorkPlacement, schedulerData } = useMemo(() => {
    const hasWorkPlacement = data?.courseItems.some(
      (item) => item.unitOfCompetency?.work_placement === true,
    );
    const schedulerData =
      Array.isArray(data?.schedule) && data?.schedule?.length
        ? data.schedule.filter((item) => item.type === 'UNIT')
        : [];

    return { hasWorkPlacement, schedulerData };
  }, [data]);

  if (!data) {
    return (
      <VStack w="full" h="full" justify="start" align="start" gap="10px">
        <VStack w="full" justify="start" align="start" gap="6px">
          {identifier ? (
            <Text textStyle="md" color="fg.muted">
              {identifier}
            </Text>
          ) : null}
          <Box overflow="hidden" alignSelf="stretch">
            <Text textStyle="2xl" color="fg.default" fontWeight="bold">
              {title}
            </Text>
          </Box>
        </VStack>
        <Text color="fg.muted" textStyle="sm">
          <Trans>Sorry, we couldn't load the course data. Please refresh or try again later.</Trans>
        </Text>
      </VStack>
    );
  }

  return (
    <VStack w="full" h="full" justify="start" align="start" gap="10px">
      <VStack w="full" justify="start" align="start" gap="6px">
        {selectedCourseAttributes.identifier ? (
          <Text fontSize="md" color="fg.muted">
            {selectedCourseAttributes.identifier}
          </Text>
        ) : null}
        <Box overflow="hidden" alignSelf="stretch">
          <Text textStyle="2xl" color="fg.default" fontWeight="bold">
            {selectedCourseAttributes.kind === CourseKindSchema.enum.SKILL_SET
              ? `Skill set: ${selectedCourseAttributes.title}`
              : selectedCourseAttributes.title}
          </Text>
        </Box>
        <HStack gap="8px" justify="start" align="center">
          <HStack gap="4px" justify="start" align="center">
            <Text fontSize="sm" lineHeight="5" color="fg.muted">
              <Trans>Cost</Trans>:
            </Text>
            <Text fontSize="sm" lineHeight="5" color="fg.default" fontWeight="medium">
              ${selectedCourseAttributes.price_aud.toString()}
            </Text>
          </HStack>
          {hasWorkPlacement === true ? (
            <>
              <Text fontSize="sm" lineHeight="5" color="fg.subtle">
                •
              </Text>
              <Text fontSize="sm" lineHeight="5" color="warning.deep" fontWeight="medium">
                <Trans>Requires work placement</Trans>
              </Text>
            </>
          ) : null}
        </HStack>
        <Box overflow="hidden" alignSelf="stretch">
          <Text fontSize="sm" color="fg.muted">
            {selectedCourseAttributes.specialisation}
          </Text>
        </Box>
      </VStack>

      <Box w="full" h="full">
        <UnorderedList
          listStyleType="none"
          overflowY="auto"
          minHeight="100px"
          maxHeight="400px"
          p={0}
          m={0}
          w="full"
        >
          {data.description ? (
            <Text textStyle="sm" color="fg.muted" mb={4}>
              {data.description}
            </Text>
          ) : null}

          {schedulerData.length
            ? schedulerData.map((item, index: number) => (
                <ListItem key={item.id} w="full">
                  <VStack w="full" align="center" gap="0">
                    <CourseUnitOfCompetency unit={item} />
                    {index !== schedulerData.length - 1 && (
                      <Box bg="border.emphasis" w="2px" h="16px" />
                    )}
                  </VStack>
                </ListItem>
              ))
            : null}
        </UnorderedList>
      </Box>
    </VStack>
  );
}

export function ReviewCourse({ goToNextStep, goToPrevStep }: ReviewCourseProps) {
  const { selectedCourseAttributes } = useBulkInternalEnrolments();

  const { data: courseData, isLoading } = trpcTenant.courses.byId.useQuery(
    selectedCourseAttributes.id,
    {
      enabled: !!selectedCourseAttributes.id,
    },
  );

  if (!selectedCourseAttributes.id) {
    return (
      <FormMainContainer>
        <FormContentContainer>
          <Box w="full" textAlign="left">
            <Text color="fg.muted" textStyle="sm">
              <Trans>No course is selected. Please select one to continue.</Trans>
            </Text>
          </Box>
        </FormContentContainer>
        <FormFooterContainer>
          <div />
          <Button onClick={goToPrevStep} variant="tertiary" leftIcon={<ChevronLeftIcon />}>
            <Trans>Back</Trans>
          </Button>
        </FormFooterContainer>
      </FormMainContainer>
    );
  }

  return (
    <FormMainContainer>
      <FormContentContainer>
        {isLoading ? (
          <>
            <VStack w="full" justify="start" align="start" gap="6px">
              {selectedCourseAttributes.identifier ? (
                <Text fontSize="md" lineHeight="6" color="fg.muted">
                  {selectedCourseAttributes.identifier}
                </Text>
              ) : null}
              <Box overflow="hidden" alignSelf="stretch">
                <Text fontSize="2xl" lineHeight="8" color="fg.default" fontWeight="bold">
                  {selectedCourseAttributes.title}
                </Text>
              </Box>
            </VStack>
            <Center h="full" w="full">
              <Spinner color="border.accent" size="lg" />
            </Center>
          </>
        ) : (
          <CourseReview
            data={courseData}
            title={selectedCourseAttributes.title}
            identifier={selectedCourseAttributes.identifier}
          />
        )}
      </FormContentContainer>
      <FormFooterContainer>
        <Button rightIcon={<ChevronRightIcon />} onClick={goToNextStep}>
          <Trans>Next</Trans>
        </Button>
        <Button onClick={goToPrevStep} variant="tertiary" leftIcon={<ChevronLeftIcon />}>
          <Trans>Back</Trans>
        </Button>
      </FormFooterContainer>
    </FormMainContainer>
  );
}
